import * as React from "react";
import { OnComplete, GenericUpdatePanel } from "../common/SubentityPanel";
import { IPredecessorInfo, ITask, ITaskAttrs } from "../../entities/Subentities";
import { Dictionary, EntityType, ppmxTaskConnectionId } from "../../entities/common";
import * as Metadata from "../../entities/Metadata";
import { validatorsBuilder, rendersBuilder, buildDeriveDisplayAttributes, buildTaskSavePayload, LoadedTask } from "../task/Fields";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { RouteComponentProps, withRouter } from "react-router-dom";
import * as ResourcesStore from '../../store/ResourcesListStore';
import { bindActionCreators } from "redux";
import { Insights, StatusCalculationTypes } from "../../store/Tenant";
import { nameof, namesof } from "../../store/services/metadataService";
import { calculateTaskVariance, getMandatoryEditFields } from "../common/sectionsControl/uiControls/TasksControl";
import * as ProjectsListStore from '../../store/ProjectsListStore';
import { SourceType } from "../../store/ExternalEpmConnectStore";
import TaskBaseline from "../task/TaskBaseline";
import TaskPredecessors from "../task/TaskPredecessors";
import { Icon, Overlay } from "office-ui-fabric-react";
import TaskAssignments from "../task/TaskAssignments/TaskAssignments";
import Spinner from "../common/Spinner";
import { TaskCalculationModeTypes } from "../views/list/columns/task/Mode";
import { TaskTypeNames } from "../views/list/columns/task/TaskType";
import { TaskType } from "../../entities/common";
import { fetchProjectTasksForPredecessors, fetchTaskAssignmentShares } from "../task/taskFetchers";
import { Resource } from "../../store/ResourcesListStore";
import { CalendarDataSet } from "../../store/calendar/common";

export type FindTaskInfo = { 
    id: string; 
    name: string, 
    startDate?: string; 
    dueDate?: string; 
    duration?: number; 
    predecessor?: IPredecessorInfo[];
    parentIds: string[];
}

type OwnProps = {
    projectId: string;
    readOnly: boolean;
    statusCalculationDisabled: boolean;
    entity: ITask;
    fields: Metadata.Field[];
    onComplete: OnComplete;
    onDismiss: () => void;
    mandatoryEditableFields?: (entity: ITask) => string[] | undefined;
}
type StateProps = {
    project: ProjectsListStore.ProjectInfo
    insights: Insights;
    groups: Metadata.Group[];
    calendar: CalendarDataSet;
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean;
    resources: Dictionary<Resource>;
    calendarsById: Dictionary<CalendarDataSet>;
}
type ActionProps = {
    groupsActions: typeof ProjectsListStore.groupsCreators;
    resourcesActions: typeof ResourcesStore.actionCreators;
};
type Props = OwnProps & StateProps & ActionProps & RouteComponentProps<any>;

class TaskEditPanel extends React.Component<Props, { tasks: ITask[], resourcesLoaded: boolean, assignmentShares?: Dictionary<number> }> {
    private _updatePanel;
    _isMounted = false;

    constructor(props: Props) {
        super(props);
        this.state = { tasks: [], resourcesLoaded: !props.entity.attributes.AssignedTo?.length };
        this._updatePanel = GenericUpdatePanel<ITask>();
    }

    componentWillMount() {
        const { entity, projectId } = this.props;
        const connectionId = entity.connectionId ?? ppmxTaskConnectionId;

        this.props.groupsActions.load(projectId, connectionId);
        if (entity.attributes.AssignedTo) {
            this.props.resourcesActions.loadResourcesByIds(
                entity.attributes.AssignedTo.map(a => a.id),
                ResourcesStore.ResourceLoadingTypes.Calendar,
                undefined,
                undefined,
                () => this._isMounted && this.setState({ resourcesLoaded: true }));
        }
        if (connectionId === ppmxTaskConnectionId) {
            fetchProjectTasksForPredecessors(projectId)
                .then(tasks => this._isMounted && this.setState({ tasks }));
            fetchTaskAssignmentShares(projectId, connectionId, entity.id)
                .then(assignmentShares => this._isMounted && this.setState({ assignmentShares }));
        }
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const {
            entity,
            fields,
            onComplete,
            onDismiss,
            readOnly,
            insights,
            statusCalculationDisabled,
            calendar,
            mandatoryEditableFields,
            project,
            resources,
            shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay
        } = this.props;

        const calendarLoaded = (entity.connectionId ?? ppmxTaskConnectionId) !== ppmxTaskConnectionId
            || Object.keys(calendar.workDayExpectedHrs).length > 0;

        if (!project || !calendarLoaded || !this.state.resourcesLoaded) {
            return <Overlay><Spinner /></Overlay>;
        }

        return <this._updatePanel
            subentityTypeLabel="Task"
            subentityType={EntityType.Task}
            displayFields={getMandatoryEditFields(entity.sourceType, this.props.project.settings.progressCalculationType)}
            entity={entity}
            fields={fields}
            readOnly={readOnly}
            onComplete={onComplete}
            onDismiss={onDismiss}
            readOnlyFields={(task: ITask) => getTaskReadonlyFields(task, insights, statusCalculationDisabled)}
            customFieldValidatorBuilder={validatorsBuilder(calendar, resources, this.props.calendarsById, this.props.project.settings.useResourceCalendar, () => this.state.tasks, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay)}
            uiControlElementsCustomRender={rendersBuilder(project, this.props.groups, calendar, resources, this.props.calendarsById, this.props.project.settings.useResourceCalendar, this._urlBuilder, () => this.state.tasks, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay)}
            deriveDisplayAttributes={buildDeriveDisplayAttributes(project, calendar, resources, this.props.calendarsById, this.props.project.settings.useResourceCalendar, () => this.state.tasks, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, () => this.state.assignmentShares)}
            buildSavePayload={buildTaskSavePayload(project, calendar, resources, this.props.calendarsById, this.props.project.settings.useResourceCalendar)}
            mandatoryEditableFields={mandatoryEditableFields}
            extraTabs={entity.sourceType === SourceType.Ppmx
                ? [{
                    key: 'assignments',
                    label: 'Assignments',
                    renderHeaderSecondaryText: () => "Add assignment(s) for the task, then define assignment attributes.",
                    renderBody: (task, onChange) => <TaskAssignments
                        readonly={readOnly}
                        task={task as ITask}
                        onChange={onChange}
                    />
                }, {
                    key: 'predecessors',
                    label: 'Predecessors',
                    renderHeaderSecondaryText: () => !this.props.project.isPrivate
                        ? "Add predecessor(s) and external predecessor(s) for the task, then select the dependency type and optionally set lead or lag time."
                        : "Add predecessor(s) for the task, then select the dependency type and optionally set lead or lag time.",
                    renderBody: (task, onChange) => <TaskPredecessors
                        readonly={readOnly}
                        task={task as ITask}
                        tasks={this.state.tasks}
                        project={this.props.project}
                        onChange={onChange}
                        calendar={this.props.calendar}
                        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay={shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay}
                        resources={this.props.resources}
                        calendarsById={this.props.calendarsById}
                        useResourceCalendar={this.props.project.settings.useResourceCalendar}
                    />
                }, {
                    key: 'baseline',
                    label: 'Baseline',
                    renderHeaderSecondaryText: () => `View the task baseline and its current state`,
                    renderBody: (updatedTask: ITask) => <TaskBaseline task={updatedTask} variance={calculateTaskVariance(updatedTask, entity, calendar)} />
                }]
                : undefined}
            header={{ 
                suffixRender: titleSuffixRender,
                showFullScreenMode: true,
            }}
        />;
    }

    private _urlBuilder = () => undefined;
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        groupsActions: bindActionCreators(ProjectsListStore.groupsCreators, dispatch),
        resourcesActions: bindActionCreators(ResourcesStore.actionCreators, dispatch)
    }
}

export default withRouter<OwnProps>(connect(
    (state: ApplicationState, ownProp: OwnProps & RouteComponentProps<any>): StateProps => {
        const project = state.projectsList.byId[ownProp.projectId];
        return {
            calendar: state.calendars.byId[project.settings.calendar.id],
            shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay:
                state.projectsList.byId[ownProp.projectId]?.settings?.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay
                ?? true,
            groups: state.projectsList.groups.byId[ownProp.projectId]?.items || [],
            insights: state.tenant.insights,
            project,
            resources: state.resources.byId,
            calendarsById: state.calendars.byId
        };
    },
    mergeActionCreators
)(TaskEditPanel));

const readonlyAutoSummaryTaskFields = namesof<ITaskAttrs>(["StartDate", "DueDate", "Duration", "StoryPoints", "CompletedStoryPoints",
    "Progress", "Effort", "CompletedWork", "RemainingWork", "OriginalEstimate"]);

export const getTaskReadonlyFields = (entity: ITask, insights: Insights, statusCalculationDisabled?: boolean): string[] => {
    let fields = insights.task.statusCalculation === StatusCalculationTypes.Auto && !statusCalculationDisabled
        ? [nameof<ITaskAttrs>("Status")]
        : [];

    if (entity.isAutoMode && entity.hierarchy.isParent) {
        fields = fields.concat(readonlyAutoSummaryTaskFields);
    }

    return fields;
}

export const titleSuffixRender = (subentity: ITask): (JSX.Element | undefined) =>
    <>
        <a className='calculation-mode' target='_blank'
            href="https://help.ppm.express/94162-portfolio-and-project-management/2082039-project-settings#section-6"
            title='Click for more information about the calculation mode for PPM Express Tasks.' >
            {subentity.isAutoMode ? TaskCalculationModeTypes.Auto : TaskCalculationModeTypes.Manual} Mode
            <Icon iconName="PPMXQuestionMarkInCircle" />
        </a>
        <a className='calculation-mode' target='_blank'
            href="https://help.ppm.express/94162-portfolio-and-project-management/2082039-project-settings#section-6"
            title='Click for more information about the task types for PPM Express Tasks.' >
            {TaskTypeNames[subentity.taskType ?? TaskType.Flexible]}
            <Icon iconName="PPMXQuestionMarkInCircle" />
        </a>
    </>;
